import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Entre ton adresse email');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email envoyé !');
    } catch (error) {
      // On affiche toujours un succès pour éviter l'énumération d'emails
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Back to login */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </Link>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            {!sent ? (
              <>
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Mot de passe oublié ?</h1>
                  <p className="text-gray-500 mt-2">
                    Pas de panique ! Entre ton email et on t'envoie un lien pour le réinitialiser.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="email">Adresse email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ton@email.fr"
                      className="mt-2 h-12 rounded-xl"
                      autoFocus
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600"
                    disabled={loading}
                  >
                    {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
                  </Button>
                </form>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Email envoyé ! 📬</h1>
                <p className="text-gray-500 mb-6">
                  Si l'adresse <strong>{email}</strong> est associée à un compte, 
                  tu vas recevoir un email avec un lien pour réinitialiser ton mot de passe.
                </p>
                <p className="text-sm text-gray-400 mb-6">
                  Pense à vérifier tes spams si tu ne le vois pas.
                </p>
                
                <div className="space-y-3">
                  <Button 
                    variant="outline"
                    onClick={() => setSent(false)}
                    className="w-full h-12 rounded-xl"
                  >
                    Renvoyer l'email
                  </Button>
                  <Link to="/login">
                    <Button className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600">
                      Retour à la connexion
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Help text */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Tu n'as pas de compte ?{' '}
            <Link to="/register" className="text-emerald-600 hover:underline font-medium">
              Inscris-toi
            </Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
